import React, { useMemo, useState, useCallback } from "react";

/**
 * Reusable sortable-table hook.
 *
 * const { rows, sortKey, sortDir, onSort, thProps } = useSortableTable(data, {
 *   initialKey: "score", initialDir: "desc",
 *   // Optional per-column custom accessor:
 *   accessors: { name: r => r.agent_name?.toLowerCase() }
 * });
 *
 * ...
 * <thead data-sticky>
 *   <tr>
 *     <SortableTh {...thProps("name", "left")}>Agent</SortableTh>
 *     <SortableTh {...thProps("score")}>Score</SortableTh>
 *   </tr>
 * </thead>
 *
 * Null / undefined values always sort to the bottom regardless of direction.
 * Toggling the same column flips direction; clicking a new column resets to desc.
 */
export function useSortableTable(data, opts = {}) {
  // Store key + dir as ONE object so we can compute both from the same
  // previous state in a single functional updater — avoids the React 18
  // StrictMode double-invoke bug that occurs when setSortDir is nested
  // inside a setSortKey updater (each dev-mode double-invoke would
  // toggle direction twice = net no-op).
  const [sort, setSort] = useState({
    key: opts.initialKey || null,
    dir: opts.initialDir || "desc",
  });
  const sortKey = sort.key;
  const sortDir = sort.dir;
  const accessors = opts.accessors || {};

  const onSort = useCallback((key) => {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "desc" ? "asc" : "desc" };
      }
      return { key, dir: "desc" };
    });
  }, []);

  const rows = useMemo(() => {
    if (!Array.isArray(data)) return [];
    if (!sortKey) return data;
    const acc = accessors[sortKey] || ((r) => (r ? r[sortKey] : undefined));
    const mult = sortDir === "asc" ? 1 : -1;
    const arr = [...data];
    arr.sort((a, b) => {
      const av = acc(a);
      const bv = acc(b);
      const aNull = av === null || av === undefined || av === "";
      const bNull = bv === null || bv === undefined || bv === "";
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * mult;
      }
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      if (as < bs) return -1 * mult;
      if (as > bs) return 1 * mult;
      return 0;
    });
    return arr;
  }, [data, sortKey, sortDir, accessors]);

  const thProps = useCallback(
    (key, align = "right") => ({
      _sortKey: key,
      _sortActive: sortKey === key,
      _sortDir: sortKey === key ? sortDir : null,
      _align: align,
      _onSort: onSort,
    }),
    [sortKey, sortDir, onSort]
  );

  return { rows, sortKey, sortDir, onSort, thProps };
}

/**
 * <SortableTh {...thProps("key")}>Label</SortableTh>
 *
 * `data-testid` should be forwarded; extra props are spread onto <th>.
 * Consumers should NOT touch _sortKey/_sortActive/_sortDir/_align/_onSort —
 * those come from useSortableTable via thProps().
 */
export function SortableTh({
  _sortKey,
  _sortActive,
  _sortDir,
  _align = "right",
  _onSort,
  className = "",
  children,
  ...rest
}) {
  const dir = _sortActive ? (_sortDir === "desc" ? "↓" : "↑") : "";
  const alignCls = _align === "left" ? "text-left" : _align === "center" ? "text-center" : "text-right";
  const activeCls = _sortActive ? "text-slate-900" : "text-slate-500";
  return (
    <th
      onClick={() => _onSort && _onSort(_sortKey)}
      className={`py-2 px-2 cursor-pointer select-none hover:text-slate-900 transition-colors ${alignCls} ${activeCls} ${className}`}
      data-sort-key={_sortKey}
      data-sort-active={_sortActive ? "true" : "false"}
      {...rest}
    >
      {children}
      {dir ? <span className="ml-1 text-[10px] opacity-70">{dir}</span> : null}
    </th>
  );
}
